import { useEffect, useState, useCallback } from "react";
import {
  Accessibility,
  X,
  Type,
  Minus,
  Plus,
  Contrast,
  Link2,
  MousePointer2,
  RotateCcw,
  Eye,
  AlignJustify,
  PauseCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "tg_a11y_prefs_v1";

const DEFAULTS = {
  fontStep: 0,            // -2..+4 (each step = 12.5%)
  spacingStep: 0,         // 0..3
  highContrast: false,
  invert: false,
  grayscale: false,
  underlineLinks: false,
  bigCursor: false,
  readableFont: false,
  pauseAnimations: false,
};

const A11Y_STYLE_ID = "tg-a11y-runtime-style";

function applyPrefs(prefs) {
  const html = document.documentElement;
  // Font size scale via CSS variable
  const scale = 1 + prefs.fontStep * 0.125;
  html.style.setProperty("--a11y-font-scale", String(scale));
  // Letter / line spacing
  html.style.setProperty("--a11y-letter-spacing", `${prefs.spacingStep * 0.04}em`);
  html.style.setProperty("--a11y-line-height", String(1.5 + prefs.spacingStep * 0.15));

  // Toggle data attributes used by runtime stylesheet below
  html.dataset.a11yHighContrast = prefs.highContrast ? "1" : "";
  html.dataset.a11yInvert = prefs.invert ? "1" : "";
  html.dataset.a11yGrayscale = prefs.grayscale ? "1" : "";
  html.dataset.a11yUnderline = prefs.underlineLinks ? "1" : "";
  html.dataset.a11yBigCursor = prefs.bigCursor ? "1" : "";
  html.dataset.a11yReadable = prefs.readableFont ? "1" : "";
  html.dataset.a11yPause = prefs.pauseAnimations ? "1" : "";
}

function ensureStyleEl() {
  if (document.getElementById(A11Y_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = A11Y_STYLE_ID;
  style.textContent = `
    html { --a11y-font-scale: 1; --a11y-letter-spacing: 0; --a11y-line-height: 1.5; }
    body { font-size: calc(16px * var(--a11y-font-scale)); }
    body, body * { letter-spacing: var(--a11y-letter-spacing); }
    html[data-a11y-readable="1"] body, html[data-a11y-readable="1"] body * {
      font-family: Verdana, Tahoma, Arial, sans-serif !important;
      line-height: var(--a11y-line-height) !important;
    }
    html[data-a11y-high-contrast="1"] body {
      background: #000 !important;
      color: #fff !important;
    }
    html[data-a11y-high-contrast="1"] body * {
      background-color: transparent !important;
      color: #fff !important;
      border-color: #fff !important;
    }
    html[data-a11y-high-contrast="1"] body a,
    html[data-a11y-high-contrast="1"] body button,
    html[data-a11y-high-contrast="1"] body [role="button"] {
      color: #ffd60a !important;
      text-decoration: underline !important;
    }
    html[data-a11y-high-contrast="1"] body img,
    html[data-a11y-high-contrast="1"] body video,
    html[data-a11y-high-contrast="1"] body svg {
      filter: brightness(0.85) contrast(1.15);
    }
    html[data-a11y-invert="1"] body { filter: invert(1) hue-rotate(180deg); }
    html[data-a11y-invert="1"] body img,
    html[data-a11y-invert="1"] body video {
      filter: invert(1) hue-rotate(180deg);
    }
    html[data-a11y-grayscale="1"] body { filter: grayscale(1); }
    html[data-a11y-underline="1"] body a { text-decoration: underline !important; }
    html[data-a11y-big-cursor="1"], html[data-a11y-big-cursor="1"] * {
      cursor: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='%23ffd60a' stroke='%230f2a5c' stroke-width='1.5'><path d='M4 2 L20 12 L13 13 L10 21 Z'/></svg>") 4 2, auto !important;
    }
    html[data-a11y-pause="1"] *, html[data-a11y-pause="1"] *::before, html[data-a11y-pause="1"] *::after {
      animation-duration: 0s !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0s !important;
    }
    /* widget itself should never be inverted/blurred */
    #tg-a11y-widget, #tg-a11y-widget * { filter: none !important; }
  `;
  document.head.appendChild(style);
}

const Toggle = ({ active, icon: Icon, label, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-pressed={active}
    className={`flex items-center gap-3 p-3 border-2 transition-all text-left ${
      active
        ? "bg-[color:var(--brand-yellow)] border-[color:var(--brand-navy)] text-[color:var(--brand-navy)]"
        : "bg-white border-slate-200 text-slate-700 hover:border-[color:var(--brand-navy)]"
    }`}
  >
    <span className={`w-9 h-9 flex items-center justify-center shrink-0 ${active ? "bg-[color:var(--brand-navy)] text-[color:var(--brand-yellow)]" : "bg-slate-100 text-[color:var(--brand-navy)]"}`}>
      <Icon className="w-4 h-4" />
    </span>
    <span className="text-xs font-bold uppercase tracking-wider leading-tight">{label}</span>
  </button>
);

export const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULTS);

  // Hydrate from localStorage
  useEffect(() => {
    ensureStyleEl();
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (stored && typeof stored === "object") {
        setPrefs({ ...DEFAULTS, ...stored });
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Apply + persist
  useEffect(() => {
    applyPrefs(prefs);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* ignore */
    }
  }, [prefs]);

  const update = useCallback((patch) => {
    setPrefs((p) => ({ ...p, ...patch }));
  }, []);

  const reset = () => setPrefs(DEFAULTS);

  return (
    <div id="tg-a11y-widget" className="fixed left-4 bottom-4 z-[60]">
      <button
        type="button"
        data-testid="a11y-toggle-button"
        aria-label={open ? "Close accessibility menu" : "Open accessibility menu"}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-14 h-14 rounded-full bg-[color:var(--brand-navy)] text-[color:var(--brand-yellow)] ring-4 ring-[color:var(--brand-yellow)]/70 shadow-xl hover:scale-105 active:scale-95 transition-transform flex items-center justify-center focus:outline-none focus:ring-[color:var(--brand-yellow)]"
      >
        {open ? <X className="w-6 h-6" /> : <Accessibility className="w-7 h-7" />}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Accessibility tools"
          data-testid="a11y-panel"
          className="absolute left-0 bottom-16 w-[320px] sm:w-[380px] bg-white border-2 border-[color:var(--brand-navy)] shadow-2xl"
        >
          <div className="bg-[color:var(--brand-navy)] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Accessibility className="w-5 h-5 text-[color:var(--brand-yellow)]" />
              <span className="font-display font-extrabold text-sm uppercase tracking-widest">
                Accessibility
              </span>
            </div>
            <button
              type="button"
              data-testid="a11y-close-button"
              aria-label="Close accessibility menu"
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto p-5 space-y-5">
            {/* Font size */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-widest text-[color:var(--brand-navy)]">
                  Text size
                </span>
                <span className="text-xs text-slate-500">
                  {Math.round((1 + prefs.fontStep * 0.125) * 100)}%
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  data-testid="a11y-font-decrease"
                  onClick={() => update({ fontStep: Math.max(-2, prefs.fontStep - 1) })}
                  className="flex items-center justify-center p-3 border-2 border-slate-200 hover:border-[color:var(--brand-navy)]"
                  aria-label="Decrease text size"
                >
                  <Minus className="w-4 h-4 text-[color:var(--brand-navy)]" />
                </button>
                <button
                  type="button"
                  data-testid="a11y-font-reset"
                  onClick={() => update({ fontStep: 0 })}
                  className="flex items-center justify-center p-3 border-2 border-slate-200 hover:border-[color:var(--brand-navy)]"
                  aria-label="Reset text size"
                >
                  <Type className="w-4 h-4 text-[color:var(--brand-navy)]" />
                </button>
                <button
                  type="button"
                  data-testid="a11y-font-increase"
                  onClick={() => update({ fontStep: Math.min(4, prefs.fontStep + 1) })}
                  className="flex items-center justify-center p-3 border-2 border-slate-200 hover:border-[color:var(--brand-navy)]"
                  aria-label="Increase text size"
                >
                  <Plus className="w-4 h-4 text-[color:var(--brand-navy)]" />
                </button>
              </div>
            </div>

            {/* Spacing */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-widest text-[color:var(--brand-navy)]">
                  Text spacing
                </span>
                <span className="text-xs text-slate-500">Level {prefs.spacingStep}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  data-testid="a11y-spacing-decrease"
                  onClick={() => update({ spacingStep: Math.max(0, prefs.spacingStep - 1) })}
                  className="flex items-center justify-center p-3 border-2 border-slate-200 hover:border-[color:var(--brand-navy)]"
                  aria-label="Decrease text spacing"
                >
                  <Minus className="w-4 h-4 text-[color:var(--brand-navy)]" />
                </button>
                <button
                  type="button"
                  data-testid="a11y-spacing-icon"
                  onClick={() => update({ spacingStep: 0 })}
                  className="flex items-center justify-center p-3 border-2 border-slate-200 hover:border-[color:var(--brand-navy)]"
                  aria-label="Reset text spacing"
                >
                  <AlignJustify className="w-4 h-4 text-[color:var(--brand-navy)]" />
                </button>
                <button
                  type="button"
                  data-testid="a11y-spacing-increase"
                  onClick={() => update({ spacingStep: Math.min(3, prefs.spacingStep + 1) })}
                  className="flex items-center justify-center p-3 border-2 border-slate-200 hover:border-[color:var(--brand-navy)]"
                  aria-label="Increase text spacing"
                >
                  <Plus className="w-4 h-4 text-[color:var(--brand-navy)]" />
                </button>
              </div>
            </div>

            {/* Toggles grid */}
            <div className="grid grid-cols-2 gap-2">
              <Toggle
                testid="a11y-toggle-contrast"
                active={prefs.highContrast}
                icon={Contrast}
                label="High contrast"
                onClick={() => update({ highContrast: !prefs.highContrast, invert: false })}
              />
              <Toggle
                testid="a11y-toggle-invert"
                active={prefs.invert}
                icon={Eye}
                label="Invert colors"
                onClick={() => update({ invert: !prefs.invert, highContrast: false })}
              />
              <Toggle
                testid="a11y-toggle-grayscale"
                active={prefs.grayscale}
                icon={Contrast}
                label="Grayscale"
                onClick={() => update({ grayscale: !prefs.grayscale })}
              />
              <Toggle
                testid="a11y-toggle-underline"
                active={prefs.underlineLinks}
                icon={Link2}
                label="Underline links"
                onClick={() => update({ underlineLinks: !prefs.underlineLinks })}
              />
              <Toggle
                testid="a11y-toggle-cursor"
                active={prefs.bigCursor}
                icon={MousePointer2}
                label="Big cursor"
                onClick={() => update({ bigCursor: !prefs.bigCursor })}
              />
              <Toggle
                testid="a11y-toggle-readable"
                active={prefs.readableFont}
                icon={Type}
                label="Readable font"
                onClick={() => update({ readableFont: !prefs.readableFont })}
              />
              <Toggle
                testid="a11y-toggle-pause"
                active={prefs.pauseAnimations}
                icon={PauseCircle}
                label="Pause animations"
                onClick={() => update({ pauseAnimations: !prefs.pauseAnimations })}
              />
              <button
                type="button"
                data-testid="a11y-reset"
                onClick={reset}
                className="flex items-center gap-3 p-3 border-2 border-slate-200 bg-white text-slate-700 hover:border-[color:var(--brand-navy)] text-left"
              >
                <span className="w-9 h-9 flex items-center justify-center bg-slate-100 text-[color:var(--brand-navy)]">
                  <RotateCcw className="w-4 h-4" />
                </span>
                <span className="text-xs font-bold uppercase tracking-wider leading-tight">
                  Reset all
                </span>
              </button>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <Link
                to="/accessibility"
                data-testid="a11y-statement-link"
                onClick={() => setOpen(false)}
                className="block w-full text-center bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-deep)] text-white px-4 py-3 text-xs font-bold uppercase tracking-widest"
              >
                Read accessibility statement
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessibilityWidget;
